import React, { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Search } from "lucide-react";
import { supabase } from "../../services/supabaseClient";
import { loadGooglePlacesUiKit } from "../../components/UniAirLocationPicker";

// Use the very same Google Places SDK/key as the full-screen location picker.
// Geoapify remains a fallback if Google is not configured or cannot load.
const isValid = (point) =>
  point && Number.isFinite(Number(point.latitude)) && Number.isFinite(Number(point.longitude)) &&
  Number(point.latitude) >= -35.8 && Number(point.latitude) <= -21.4 &&
  Number(point.longitude) >= 15.5 && Number(point.longitude) <= 33.8;

const googleCoordinates = (value) => ({
  latitude: typeof value?.lat === "function" ? value.lat() : value?.lat ?? value?.latitude,
  longitude: typeof value?.lng === "function" ? value.lng() : value?.lng ?? value?.longitude,
});

export default function PrivateRideInlineLocationField({
  id, title, placeholder, point, displayText = "", autoFocus = false, searchScope = "south_africa",
  onSelect, onTextChange, onMapPin, disabled = false,
}) {
  const rootRef = useRef(null);
  const requestRef = useRef(0);
  const sessionRef = useRef(null);
  const [text, setText] = useState(point?.label || point?.formattedAddress || displayText || "");
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setText(point?.label || point?.formattedAddress || displayText || "");
  }, [point?.label, point?.formattedAddress, displayText]);

  useEffect(() => {
    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setFocused(false);
    };
    document.addEventListener("pointerdown", onOutside);
    return () => document.removeEventListener("pointerdown", onOutside);
  }, []);

  useEffect(() => {
    const query = text.trim();
    if (!focused || disabled || query.length < 3 || query === (point?.label || point?.formattedAddress || "")) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    const sequence = ++requestRef.current;
    setSearching(true);
    setError("");
    const timer = window.setTimeout(async () => {
      try {
        let suggestions = null;
        const googleKey = String(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
        if (googleKey) {
          try {
            const { AutocompleteSuggestion, AutocompleteSessionToken } = await loadGooglePlacesUiKit();
            if (typeof AutocompleteSuggestion?.fetchAutocompleteSuggestions !== "function") {
              throw new Error("Google Places autocomplete unavailable");
            }
            if (!sessionRef.current) sessionRef.current = new AutocompleteSessionToken();
            const { suggestions: googleSuggestions } =
              await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: query,
                includedRegionCodes: ["za"],
                region: "za",
                language: "en",
                // Bias towards Stellenbosch; results can still be anywhere in South Africa.
                locationBias: { center: { lat: -33.933, lng: 18.86 }, radius: 50000 },
                sessionToken: sessionRef.current,
              });
            suggestions = (googleSuggestions || [])
              .filter((item) => item.placePrediction)
              .slice(0, 7)
              .map((item) => {
                const prediction = item.placePrediction;
                return {
                  provider: "google",
                  key: prediction.placeId,
                  label: prediction.mainText?.text ||
                    String(prediction.text?.toString?.() || "").split(",")[0] ||
                    "Place",
                  formattedAddress: prediction.secondaryText?.text ||
                    String(prediction.text?.toString?.() || ""),
                  prediction,
                };
              });
          } catch (googleError) {
            console.warn("UniAir inline Google Places search unavailable; using location-search fallback.", googleError);
            sessionRef.current = null;
          }
        }
        // Empty Google results should not leave the search blank; try the
        // existing location-search provider before showing no matches.
        if (suggestions === null || suggestions.length === 0) {
          const { data, error: searchError } = await supabase.functions.invoke("uniair-location-search", {
            body: { action: "suggest", input: query, search_scope: searchScope },
          });
          if (searchError || !data?.ok) {
            throw new Error(data?.message || searchError?.message || "Search unavailable");
          }
          suggestions = (Array.isArray(data.suggestions) ? data.suggestions : []).slice(0, 7)
            .map((item) => ({ ...item, provider: "geoapify", key: item.providerFeatureId }));
        }
        if (!cancelled && sequence === requestRef.current) setResults(suggestions);
      } catch (searchError) {
        if (!cancelled && sequence === requestRef.current) {
          setResults([]);
          setError("We couldn't load places. Try again or use the map pin.");
        }
      } finally {
        if (!cancelled && sequence === requestRef.current) setSearching(false);
      }
    }, 320);

    return () => {
      cancelled = true;
      ++requestRef.current;
      window.clearTimeout(timer);
    };
  }, [focused, disabled, text, point?.label, point?.formattedAddress, searchScope]);

  const updateText = (next) => {
    setText(next);
    setError("");
    setResults([]);
    onTextChange(next);
    if (next.trim().length === 0) sessionRef.current = null;
  };

  const choose = async (suggestion) => {
    if (resolving || disabled) return;
    ++requestRef.current;
    setResolving(true);
    setSearching(false);
    setError("");
    try {
      let next;
      if (suggestion.provider === "google") {
        // A PlacePrediction carries the same billing session into fetchFields.
        const place = suggestion.prediction.toPlace();
        await place.fetchFields({
          fields: ["displayName", "formattedAddress", "location", "primaryType"],
        });
        const pointCoords = googleCoordinates(place.location);
        next = {
          label: String(place.displayName || suggestion.label || "").trim(),
          formattedAddress: String(place.formattedAddress || suggestion.formattedAddress || "").trim(),
          ...pointCoords,
          provider: "google",
          providerFeatureId: place.id || suggestion.key,
          source: "search",
          resultType: String(place.primaryType || "poi").toLowerCase(),
          isSpecific: true,
          originalLabel: String(place.displayName || suggestion.label || "").trim(),
        };
        sessionRef.current = null;
      } else {
        const { data, error: resolveError } = await supabase.functions.invoke("uniair-location-search", {
          body: {
            action: "retrieve",
            place_id: suggestion.providerFeatureId,
            fallback_label: suggestion.label,
            fallback_formatted_address: suggestion.formattedAddress,
            fallback_result_type: suggestion.resultType,
            search_scope: searchScope,
          },
        });
        if (resolveError || !data?.ok) throw new Error(data?.message || "Could not confirm that location.");
        const location = data.location || data.result;
        next = {
          ...location,
          label: location?.label || suggestion.label,
          formattedAddress: location?.formattedAddress || suggestion.formattedAddress || "",
          latitude: Number(location?.latitude),
          longitude: Number(location?.longitude),
          source: "search",
        };
      }
      if (!next?.label || !isValid(next)) throw new Error("We couldn't confirm that location. Choose another result or adjust the map pin.");
      // Airport Rides may validate the chosen place against their booking area.
      // Wait for that result before closing the suggestions; Private Rides can
      // continue using their synchronous selection callback unchanged.
      await onSelect(next);
      setText(next.label);
      setFocused(false);
      setResults([]);
    } catch (resolveError) {
      setError(resolveError.message || "Could not confirm this address. Try another place.");
    } finally {
      setResolving(false);
    }
  };

  const showResults = !disabled && focused && text.trim().length >= 3 &&
    text.trim() !== (point?.label || point?.formattedAddress || "");

  return (
    <div ref={rootRef} className={`relative min-w-0 flex-1 ${focused ? "z-50" : "z-10"}`}>
      <label htmlFor={id} className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-gray-500">
        {title}
      </label>
      <div className="flex min-w-0 items-center gap-2">
        <input
          id={id}
          type="search"
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled || resolving}
          spellCheck={false}
          value={text}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onChange={(event) => updateText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setFocused(false);
            if (event.key === "Enter" && showResults) {
              event.preventDefault();
              if (results.length === 1) void choose(results[0]);
            }
          }}
          className="min-w-0 flex-1 bg-transparent py-1 text-[15px] font-semibold leading-6 text-gray-950 outline-none placeholder:font-medium placeholder:text-gray-400"
        />
        {(searching || resolving) && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" aria-label="Searching locations" />}
        <button
          type="button"
          onClick={onMapPin}
          disabled={disabled || resolving}
          aria-label={"Choose " + title.toLowerCase() + " on map"}
          title="Set or adjust the pin on the map"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-200 hover:text-[#720e1e]"
        >
          <MapPin className="h-[18px] w-[18px]" />
        </button>
      </div>
      {showResults && (
        <div role="listbox" aria-label={title + " search results"}
          className="absolute left-0 right-0 top-full z-[100] mt-2 max-h-64 overflow-y-auto rounded-2xl border border-gray-200 bg-white py-1 shadow-xl">
          {searching && results.length === 0 ? (
            <div className="px-4 py-4 text-sm text-gray-500">Finding places…</div>
          ) : error ? (
            <div className="px-4 py-3 text-xs text-red-700">{error}</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">No matching places. Try an address or residence name.</div>
          ) : results.map((suggestion) => (
            <button
              key={suggestion.key}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => void choose(suggestion)}
              disabled={resolving}
              className="flex w-full items-start gap-3 border-b border-gray-50 px-3 py-3 text-left last:border-0 hover:bg-[#720e1e]/5 disabled:opacity-60"
            >
              <Search className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-gray-900">{suggestion.label}</span>
                {suggestion.formattedAddress && <span className="block truncate text-xs text-gray-500">{suggestion.formattedAddress}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
